<template>
    <div class="hyper-login" :style="`background-image:url(${VUE_APP_BG});background-size:cover`">
        <div class="auth-wrapper auth-v1 px-2">
            <div style="position:absolute;width:100%;background-color:inherit;padding:1.5rem;top:0">
                <img :src="VUE_APP_LOGO" @error="handleImageError" />
            </div>
            <div class="auth-inner py-2">
                <b-card class="mb-0" style="z-index:10">
                    <b-link class="brand-logo">
                        <h2 class="brand-text" style="margin:auto 0">
                            Hyper<span style="font-size:xx-large">Auth</span>
                        </h2>
                    </b-link>

                    <!-- form -->
                    <validation-observer
                        ref="loginValidation"
                        #default="{invalid}"
                    >
                        <b-form
                            v-if="!needOtpLogin"
                            class="auth-login-form mt-2"
                            @submit.prevent
                        >

                            <!-- email -->
                            <b-form-group
                                label-for="email"
                                label="Email"
                            >
                                <validation-provider
                                    #default="{ errors }"
                                    name="Email"
                                    rules="required|email"
                                >
                                    <b-form-input
                                        id="email"
                                        v-model="userEmail"
                                        name="login-email"
                                        :state="errors.length > 0 ? false:null"
                                        placeholder="Email"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <!-- password -->
                            <b-form-group>
                                <div class="d-flex justify-content-between">
                                    <label for="password">Password</label>
                                    <b-link :to="{name:'auth-forgot-password'}">
                                        <small>Forgot Password?</small>
                                    </b-link>
                                </div>
                                <validation-provider
                                    #default="{ errors }"
                                    name="Password"
                                    rules="required"
                                >
                                    <b-input-group
                                        class="input-group-merge"
                                        :class="errors.length > 0 ? 'is-invalid':null"
                                    >
                                        <b-form-input
                                            id="password"
                                            v-model="password"
                                            :type="passwordFieldType"
                                            class="form-control-merge"
                                            :state="errors.length > 0 ? false:null"
                                            name="login-password"
                                            placeholder="Password"
                                        />

                                        <b-input-group-append is-text>
                                            <feather-icon
                                                class="cursor-pointer"
                                                :icon="passwordToggleIcon"
                                                @click="togglePasswordVisibility"
                                            />
                                        </b-input-group-append>
                                    </b-input-group>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                            <!-- checkbox -->
                            <b-form-group>
                                <div class="d-flex justify-content-between">
                                    <b-form-checkbox
                                        id="remember-me"
                                        v-model="status"
                                        name="checkbox-1"
                                    >
                                        Remember Me
                                    </b-form-checkbox>
                                    <b-form-select
                                        v-model="selectedLocale"
                                        :options="locales"
                                        size="sm"
                                        style="width:100px"
                                    />
                                </div>
                            </b-form-group>

                            <!-- submit button -->
                            <b-button
                                variant="primary"
                                type="submit"
                                block
                                :disabled="invalid || submitted"
                                @click="validationForm"
                            >
                                Sign in
                            </b-button>
                        </b-form>
                        <b-form
                            v-else
                            @submit.prevent
                        >
                            <div class="mb-50 d-flex justify-content-end">
                                <TowFactorTimer
                                    :seconds="3 * 60"
                                    @done="doneTimer"
                                ></TowFactorTimer>
                            </div>
                            <b-form-group
                                label-for="otpCode"
                                label="Two-Factor Token"
                            >
                                <b-form-input
                                    id="otp"
                                    v-model="otpCode"
                                    name="otp-code"
                                    placeholder="OTP"
                                    autofocus
                                />
                            </b-form-group>
                            <!-- submit button -->
                            <b-button
                                variant="primary"
                                type="submit"
                                block
                                :disabled="invalid || submitted"
                                @click="validationOtpForm"
                            >
                                Send
                            </b-button>
                        </b-form>
                    </validation-observer>
                </b-card>
            </div>
        </div>
        <b-card-footer
            :style="`background-color:${reverseTheme};'`"
            class="d-block w-100 text-center"
            style="z-index:10;position:fixed;bottom:0;"
        >
            <div>
                <span class="mr-1">
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.MEGAZONE`) }}
                </span>
                <span class="mr-1">
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.Business Registration No`) }}
                </span>
                <span class="mr-1">
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.CEO`) }}
                </span>
                <a
                    :href="`/privacy?locale=${selectedLocale}&theme=${skin}`"
                    target="_blank"
                    style="vertical-align:text-bottom"
                >
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.Privacy Notice`) }}
                </a>
            </div>
            <div>
                <span class="mr-1">
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.MEGAZONE B/D, 46, Nonhyeon-ro 85-gil, Gangnam-gu, Seoul, South Korea`) }}
                </span>
                <span class="mr-1">
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.Contact Call`) }}
                </span>
                <span>
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.Contact Email`) }}
                </span>
            </div>
            <div>
                <span style="font-style:italic">
                    {{ $t(`org.footer.${VUE_APP_NAMESPACE}.Copyright`) }}
                </span>
            </div>
        </b-card-footer>
    </div>
</template>

<script>
import md5 from 'md5'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import useAppConfig from '@core/app-config/useAppConfig'
import { computed, getCurrentInstance, ref, watch } from '@vue/composition-api'
import TowFactorTimer from '@/components/Login/TwoFactorTimer'
import utils from '@/auth/utils'
export default {
    components: {
        ValidationProvider,
        ValidationObserver,
        TowFactorTimer
    },
    mixins: [
        togglePasswordVisibility
    ],
    setup () {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { skin } = useAppConfig()
        const reverseTheme = computed(() => skin.value === 'light' ? '#fff' : '#283046')
        const authToken = ref('')
        const status = ref(!!localStorage.getItem('save-id'))
        const userEmail = ref(localStorage.getItem('save-id') || '')
        const password = ref('')
        const needOtpLogin = ref(false)
        const otpCode = ref('')
        const otpVerificationTry = ref(0)
        const submitted = ref(false)
        const selectedLocale = ref(localStorage.getItem('locale'))
        /* const VUE_APP_LOGO = process.env.VUE_APP_LOGO || 'https://s3-ap-northeast-1.amazonaws.com/dohwan.bill/images/mzc_white.png' */
        const VUE_APP_NAMESPACE = process.env.VUE_APP_NAMESPACE
        const VUE_APP_LOGO_BASE_URL = process.env.VUE_APP_LOGO_BASE_URL
        const VUE_APP_LOGO = ref(`${process.env.VUE_APP_LOGO_BASE_URL}/logo/${md5(VUE_APP_NAMESPACE + '-' + process.env.VUE_APP_SERVICE)}.png`)
        const VUE_APP_BG = ref(`${process.env.VUE_APP_LOGO_BASE_URL}/bg/${md5(VUE_APP_NAMESPACE + '-' + process.env.VUE_APP_SERVICE)}.png`)
        const VUE_APP_DEFAULT_IMG = `${VUE_APP_LOGO_BASE_URL}/hyperbilling-light-logo.png`
        const locales = [
            {
                value: 'ko',
                img: require('@/assets/images/flags/ko.png'),
                text: '한국어'
            },
            {
                value: 'en',
                img: require('@/assets/images/flags/en.png'),
                text: 'English'
            },
            {
                value: 'ja',
                img: require('@/assets/images/flags/ja.png'),
                text: '日本語'
            }
        ]
        const authMessage = computed(() => store.getters.authMessage)
        const authUserResource = computed(() => store.getters.authUserResource)
        const passwordToggleIcon = computed(() => vm.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon')
        const validationForm = () => {
            vm.$refs.loginValidation.validate().then(success => {
                submitted.value = true
                if (success) {
                    store.dispatch('createMemberAuth', {
                        email: userEmail.value,
                        password: password.value
                    }).then(response => {
                        if (status.value) {
                            localStorage.setItem('save-id', userEmail.value)
                        }
                        authToken.value = response.token
                        localStorage.setItem('x-auth-token', authToken.value)
                        needOtpLogin.value = response.needOtpLogin
                        const nextPath = utils.getRoutePrerequisites()
                        if (!needOtpLogin.value) {
                            vm.$router.push(nextPath)
                        }
                        submitted.value = false
                    }).catch(error => {
                        status.value = false
                        vm.$refs.loginValidation.setErrors(error?.response?.data)
                        submitted.value = false
                    })
                }
            })
        }
        const validationOtpForm = () => {
            vm.$refs.loginValidation.validate().then(success => {
                submitted.value = true
                if (success) {
                    otpVerificationTry.value++
                    store.dispatch('createMemberAuthOtp', { otpCode: otpCode.value }).then((response) => {
                        const nextPath = utils.getRoutePrerequisites()
                        vm.$router.push(nextPath)
                    }).catch(error => {
                        localStorage.setItem('x-auth-token', authToken.value)
                        status.value = false
                        vm.$refs.loginValidation.setErrors(error?.response?.data)
                        submitted.value = false
                        userEmail.value = localStorage.getItem('save-id') || ''
                        password.value = ''
                        otpCode.value = ''
                        if (otpVerificationTry.value === 3) {
                            doneTimer()
                        }
                    })
                }
            })
        }
        const doneTimer = () => {
            needOtpLogin.value = false
        }
        const handleImageError = (event) => {
            if (event.target.src !== VUE_APP_DEFAULT_IMG) {
                VUE_APP_LOGO.value = VUE_APP_DEFAULT_IMG
            }
        }
        /* watch */
        watch(selectedLocale, (value) => {
            vm.$i18n.locale = value
            localStorage.setItem('locale', value)
        })
        watch(status, (value) => {
            if (!value) {
                localStorage.removeItem('save-id')
            }
        })

        return {
            VUE_APP_NAMESPACE,
            VUE_APP_LOGO_BASE_URL,
            VUE_APP_LOGO,
            VUE_APP_BG,
            required,
            email,
            userEmail,
            password,
            otpCode,
            status,
            submitted,
            selectedLocale,
            locales,
            skin,
            reverseTheme,
            authMessage,
            authUserResource,
            passwordToggleIcon,
            validationForm,
            validationOtpForm,
            needOtpLogin,
            doneTimer,
            handleImageError
        }
    }
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
.hyper-login:before {
    content: '';
    width: 100%;
    height: 100vh;
    position: absolute;
    opacity: 0.1;
    background-color: #fff;
    background-repeat: no-repeat;
}
[dir] .auth-wrapper.auth-v1 .auth-inner::before {
    background-image: none
}
[dir] .auth-wrapper.auth-v1 .auth-inner::after {
    background-image: none
}
</style>
